import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  InformationCircleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DateTimeInput } from '@/components/ui/DateTimeInput';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { cn } from '@/utils/cn';
import { toDateTimeLocalValue } from '@/utils/datetime';
import { incidentsService, CreateIncidentData } from '../services/incidents.service';
import { UserPicker } from '@/components/common/UserPicker';
import { SECURITY_CATEGORIES } from '@corporate-security/types';

interface FormData {
  title: string;
  description: string;
  severity: 'MINIMAL' | 'MODERATE' | 'HIGH';
  category: string;
  location: string;
  occurredAt: string;
  affectedSystems: string[];
  tags: string[];
  supervisorId: string;
  supervisorName: string;
  supervisorEmail: string;
}

interface FormErrors {
  title?: string;
  description?: string;
  severity?: string;
  category?: string;
  location?: string;
  occurredAt?: string;
  affectedSystems?: string;
  tags?: string;
  general?: string;
}

export default function CreateIncident() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    severity: 'MINIMAL',
    category: '',
    location: '',
    occurredAt: '',
    affectedSystems: [],
    tags: [],
    supervisorId: '',
    supervisorName: '',
    supervisorEmail: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [newSystem, setNewSystem] = useState('');
  const [newTag, setNewTag] = useState('');

  const severityOptions = [
    { value: 'MINIMAL', label: 'Minimal', color: 'text-success-600', bgColor: 'bg-success-50 border-success-200' },
    { value: 'MODERATE', label: 'Moderate', color: 'text-warning-600', bgColor: 'bg-warning-50 border-warning-200' },
    { value: 'HIGH', label: 'High', color: 'text-danger-600', bgColor: 'bg-danger-50 border-danger-200' },
  ];

  const categoryOptions = SECURITY_CATEGORIES;

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 200) {
      newErrors.title = 'Title must be less than 200 characters';
    }

    // Better HTML stripping that handles TipTap empty states
    const plainTextDescription = formData.description
      .replace(/<br\s*\/?>/gi, '')           // Remove <br> tags
      .replace(/<[^>]*>/g, '')                // Remove all HTML tags
      .replace(/&nbsp;/gi, ' ')               // Replace &nbsp; with space
      .trim();

    if (!plainTextDescription) {
      newErrors.description = 'Description is required';
    } else if (plainTextDescription.length < 5) {
      newErrors.description = 'Description must be at least 5 characters';
    }

    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormErrors, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAddSystem = () => {
    if (newSystem.trim() && !formData.affectedSystems.includes(newSystem.trim())) {
      setFormData(prev => ({
        ...prev,
        affectedSystems: [...prev.affectedSystems, newSystem.trim()],
      }));
      setNewSystem('');
    }
  };

  const handleRemoveSystem = (system: string) => {
    setFormData(prev => ({
      ...prev,
      affectedSystems: prev.affectedSystems.filter(s => s !== system),
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      // Scroll to first error so user sees it
      const firstError = document.querySelector('.text-danger-600');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const incidentData: CreateIncidentData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        severity: formData.severity,
        category: formData.category.trim() ?? undefined,
        location: formData.location.trim() ?? undefined,
        occurredAt: formData.occurredAt || undefined,
        affectedSystems: formData.affectedSystems.length > 0 ? formData.affectedSystems : undefined,
        tags: formData.tags.length > 0 ? formData.tags : undefined,
        ...(formData.supervisorId
          ? {
              supervisor: {
                id: formData.supervisorId,
                name: formData.supervisorName,
                email: formData.supervisorEmail,
              },
            }
          : {}),
      };

      const newIncident = await incidentsService.createIncident(incidentData);

      setSuccess(true);

      // Redirect to the new incident after a short delay
      setTimeout(() => {
        navigate(`/security/incidents/${newIncident.id}`);
      }, 2000);

    } catch (error: unknown) {
      const err = error as Error;
      console.error('Failed to create incident:', err);
      setErrors({
        general: err.message || 'Failed to create incident. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="p-4 sm:p-6"
      >
        <Card className="max-w-md mx-auto mt-20">
          <CardContent className="p-12 text-center">
            <CheckCircleIcon className="w-16 h-16 text-success-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">
              Incident Created Successfully
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400 mb-6">
              Your incident has been reported and assigned a tracking ID. You will be redirected to the incident details shortly.
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => navigate('/security/incidents')}
                className="flex-1"
              >
                View All Incidents
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <div className="flex items-center space-x-4 mb-6">
          <Link
            to="/security/incidents"
            className="p-2 rounded-lg bg-secondary-100 dark:bg-secondary-800 hover:bg-secondary-200 dark:hover:bg-secondary-700 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
              Report New Incident
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Provide details about the security incident you want to report
            </p>
          </div>
        </div>
      </motion.div>

      {/* Form */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-primary-600" />
              <span>Incident Information</span>
            </CardTitle>
            <CardDescription>
              Please provide accurate and detailed information about the incident. All fields marked with * are required.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* General Error */}
              {errors.general && (
                <div className="p-4 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <ExclamationTriangleIcon className="w-5 h-5 text-danger-600 dark:text-danger-400" />
                    <p className="text-danger-800 dark:text-danger-200">{errors.general}</p>
                  </div>
                </div>
              )}

              {/* Title */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                  Incident Title *
                </label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Brief, descriptive title of the incident"
                  className={cn(
                    'w-full',
                    errors.title && 'border-danger-300 dark:border-danger-600 focus:border-danger-500 focus:ring-danger-500'
                  )}
                  maxLength={200}
                />
                {errors.title && (
                  <p className="text-sm text-danger-600 dark:text-danger-400">{errors.title}</p>
                )}
                <p className="text-xs text-secondary-500">
                  {formData.title.length}/200 characters
                </p>
              </div>

              {/* Description */}
              <RichTextEditor
                label="Description"
                required
                value={formData.description}
                onChange={(html) => handleInputChange('description', html)}
                placeholder="Detailed description of what happened, when it occurred, and any relevant context..."
                error={errors.description}
                minHeight={200}
              />

              {/* Severity */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                  Severity Level *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {severityOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleInputChange('severity', option.value)}
                      className={cn(
                        'p-3 border-2 rounded-lg text-left transition-all',
                        'hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500',
                        formData.severity === option.value
                          ? `${option.bgColor} ${option.color} border-current`
                          : 'bg-white dark:bg-secondary-800 border-secondary-200 dark:border-secondary-600 text-secondary-700 dark:text-secondary-300'
                      )}
                    >
                      <div className="font-medium">{option.label}</div>
                      <div className="text-xs opacity-70 mt-1">
                        {option.value === 'MINIMAL' && 'Minor impact, routine handling'}
                        {option.value === 'MODERATE' && 'Moderate impact, timely response needed'}
                        {option.value === 'HIGH' && 'Significant impact, urgent attention required'}
                      </div>
                    </button>
                  ))}
                </div>
                {errors.severity && (
                  <p className="text-sm text-danger-600 dark:text-danger-400">{errors.severity}</p>
                )}
              </div>

              {/* Category */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className={cn(
                    'w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg',
                    'bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white',
                    'focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                    errors.category && 'border-danger-300 dark:border-danger-600 focus:border-danger-500 focus:ring-danger-500'
                  )}
                >
                  <option value="">Select a category...</option>
                  {categoryOptions.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <p className="text-sm text-danger-600 dark:text-danger-400">{errors.category}</p>
                )}
              </div>

              {/* Supervisor */}
              <UserPicker
                label="Supervisor (Optional)"
                value={formData.supervisorId}
                roleFilter={['SECURITY_MANAGER', 'ADMIN', 'SUPER_ADMIN']}
                onChange={(picked) => {
                  setFormData({
                    ...formData,
                    supervisorId: picked?.id ?? '',
                    supervisorName: picked?.name ?? '',
                    supervisorEmail: picked?.email ?? '',
                  });
                }}
              />

              {/* Occurred Date */}
              <div className="space-y-2">
                <DateTimeInput
                  label="Date/Time Occurred (Optional)"
                  value={formData.occurredAt}
                  onChange={(value) => handleInputChange('occurredAt', value)}
                  max={toDateTimeLocalValue(new Date())}
                  className="w-full"
                />
                <p className="text-xs text-secondary-500 flex items-center space-x-1">
                  <InformationCircleIcon className="w-3 h-3" />
                  <span>When did this incident actually occur? Leave blank if same as report time.</span>
                </p>
              </div>

              {/* Location */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                  Location (Optional)
                </label>
                <Input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder="Building, floor, room, or general area where the incident occurred"
                  className="w-full"
                />
                <p className="text-xs text-secondary-500 flex items-center space-x-1">
                  <InformationCircleIcon className="w-3 h-3" />
                  <span>Include any relevant location details to help with investigation</span>
                </p>
              </div>

              {/* Affected Systems */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                  Affected Systems (Optional)
                </label>
                <div className="flex space-x-2">
                  <Input
                    type="text"
                    value={newSystem}
                    onChange={(e) => setNewSystem(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddSystem();
                      }
                    }}
                    placeholder="Enter a system name (e.g., Active Directory, VPN Gateway)"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={handleAddSystem}
                    variant="outline"
                    disabled={!newSystem.trim()}
                  >
                    Add
                  </Button>
                </div>
                {formData.affectedSystems.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.affectedSystems.map((system) => (
                      <span
                        key={system}
                        className="px-3 py-1 text-sm bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300 rounded-full flex items-center space-x-2"
                      >
                        <span>{system}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveSystem(system)}
                          className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-secondary-500 flex items-center space-x-1">
                  <InformationCircleIcon className="w-3 h-3" />
                  <span>List any systems, applications, or infrastructure affected by this incident</span>
                </p>
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                  Tags (Optional)
                </label>
                <div className="flex space-x-2">
                  <Input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    placeholder="Enter a tag (e.g., phishing, data-breach, critical)"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={handleAddTag}
                    variant="outline"
                    disabled={!newTag.trim()}
                  >
                    Add
                  </Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 text-sm bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300 rounded-full flex items-center space-x-2"
                      >
                        <span>#{tag}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-800 dark:hover:text-secondary-200"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-secondary-500 flex items-center space-x-1">
                  <InformationCircleIcon className="w-3 h-3" />
                  <span>Add tags to help categorize and find this incident later</span>
                </p>
              </div>

              {/* Form Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-secondary-200 dark:border-secondary-700">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/security/incidents')}
                  disabled={loading}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto bg-primary-600 hover:bg-primary-700"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Creating Incident...</span>
                    </div>
                  ) : (
                    'Create Incident'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}